import {
  IWorkflowAgentStepAttachmentView,
  uploadAgentStepAttachment,
  WORKFLOW_AGENT_STEP_ATTACHMENT_EXTENSIONS,
  WORKFLOW_AGENT_STEP_ATTACHMENT_MAX_BYTES,
} from '@commenda/api';
import { formatBytesString } from '@commenda/kakashi';
import {
  messageFromApiErrorResponse,
  readJsonResponseAs,
} from '@commenda/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

const SLOW_EXTRACTION_NOTICE_MS = 4000;

const extensionOf = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

interface UseAgentStepAttachmentUploadParams {
  onAttached: (attachment: IWorkflowAgentStepAttachmentView) => void;
}

export const useAgentStepAttachmentUpload = ({
  onAttached,
}: UseAgentStepAttachmentUploadParams) => {
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [isSlowExtraction, setIsSlowExtraction] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lastExtractedAttachment, setLastExtractedAttachment] =
    useState<IWorkflowAgentStepAttachmentView | null>(null);

  useEffect(
    () => () => {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    },
    []
  );

  const onFileSelected = useCallback(
    async (file: File) => {
      setUploadError(null);
      setLastExtractedAttachment(null);

      if (file.size === 0) {
        setUploadError('That file is empty.');
        return;
      }

      if (file.size > WORKFLOW_AGENT_STEP_ATTACHMENT_MAX_BYTES) {
        setUploadError(
          `“${file.name}” is ${formatBytesString(file.size)}. An attached file must be ${formatBytesString(WORKFLOW_AGENT_STEP_ATTACHMENT_MAX_BYTES)} or smaller.`
        );
        return;
      }

      // Catches .csv and anything else the server has no extractor for, without making the
      // author wait out a PDF-length round trip to be told so.
      if (
        !WORKFLOW_AGENT_STEP_ATTACHMENT_EXTENSIONS.has(extensionOf(file.name))
      ) {
        setUploadError(
          `Commenda cannot read “${file.name}”. A step resource must be a PDF, a Word document (.doc, .docx), a spreadsheet (.xlsx, .xls, .xlsm, .xlsb, .ods), rich text (.rtf) or plain text (.txt, .md). Convert it to one of those and attach it again.`
        );
        return;
      }

      setPendingFileName(file.name);
      setIsSlowExtraction(false);
      slowTimerRef.current = setTimeout(
        () => setIsSlowExtraction(true),
        SLOW_EXTRACTION_NOTICE_MS
      );

      let response: Response;
      try {
        response = await uploadAgentStepAttachment(file);
      } catch {
        setUploadError(
          'The file could not be sent. Check your connection and try again.'
        );
        setPendingFileName(null);
        if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
        return;
      }

      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
      setPendingFileName(null);
      setIsSlowExtraction(false);

      if (!response.ok) {
        // All six server refusals arrive as a plain message and each already names the file and the
        // fix, so they are surfaced verbatim with no status-code mapping of our own.
        setUploadError(
          await messageFromApiErrorResponse(
            response,
            'The file could not be attached. Try again.'
          )
        );
        return;
      }

      const body = await readJsonResponseAs<{
        attachment: IWorkflowAgentStepAttachmentView;
      }>(response);

      if (!body?.attachment) {
        setUploadError('The file was attached but could not be read back.');
        return;
      }

      setLastExtractedAttachment(body.attachment);
      // Only now does the step definition learn about the file. Nothing is written on failure, so
      // an unreadable attachment cannot be saved onto the step.
      onAttached(body.attachment);
    },
    [onAttached]
  );

  return {
    pendingFileName,
    isSlowExtraction,
    uploadError,
    lastExtractedAttachment,
    onFileSelected,
  };
};
